import { message, Pagination, TableColumnsType, Tabs, Typography } from "antd";
import { ContentWrapper, DataTable } from "~/Components";
// import "../../../index.css";
import { Action, TableDrawer } from "@components/TableDrawer/TableDrawer";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { drilldownData } from "@utils/drilldownData";
import { debounce } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { get, put } from "~/Services";
import { getCurrentModule, getModulePath } from "~/Utils/StringConstants";
import { renderActionsColumn, renderDateColumn, renderUpdatedAtColumn } from "~/Utils/TableRenderFunction";
import ActionDropdown from "../StatusInformation/ActionDropdown";
import { formatSubModule, generateFieldsFromPayload, unformatSubModule } from "../StatusInformation/StatusInformation";

export const ApprovalManagement = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchString, setSearchString] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [data, setData] = useState<any>([]);
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [orderByName, setOrderByName] = useState<string>("createdAt");

  const [approvalId, setApprovalId] = useState<string | undefined>(selectedRowData?.approvalId);

  // single global actionType
  const [actionType, setActionType] = useState<string>();

  const currentModule = getCurrentModule();

  useEffect(() => {
    setApprovalId(selectedRowData?.approvalId);
  }, [selectedRowData]);

  useEffect(() => {
    if (!query.get("activeTab")) {
      if (currentModule === "ERAMAN") {
        navigate(`/dedicated/${getModulePath(currentModule)}/feedback-submissions?activeTab=enquiries`, {
          replace: true,
        });
      } else {
        navigate(`/dedicated/${getModulePath(currentModule)}/approval-management?activeTab=pending`, {
          replace: true,
        });
      }
    }
  }, []);

  const {
    data: drilldown,
    isLoading: drilldownLoading,
    refetch: refetchDocument,
  } = useDocumentDetails(approvalId || "");

  function capitalizeFirstLetter(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  useEffect(() => {
    if (approvalId) {
      refetchDocument();
    }
  }, [approvalId]);

  const renderSubModule = (text: string) => capitalizeFirstLetter(text);

  const columns: TableColumnsType = [
    {
      title: "Request Type",
      dataIndex: "action",
      key: "action",
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "module"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "module"
            ? "descend"
            : undefined,
    },
    {
      title: "Sub Module",
      dataIndex: "subModule",
      key: "subModule",
      render: (text: string) => formatSubModule(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "subModule"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "subModule"
            ? "descend"
            : undefined,
    },
    {
      title: "Requested At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "createdAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "createdAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Requested By",
      dataIndex: "createdBy",
      key: "createdBy",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "createdBy"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "createdBy"
            ? "descend"
            : undefined,
    },
    {
      title: "Approved At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "createdBy"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "createdBy"
            ? "descend"
            : undefined,
    },
    {
      title: "Rejected At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Approved By",
      dataIndex: "updatedBy",
      key: "updatedBy",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedBy"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedBy"
            ? "descend"
            : undefined,
      render: (text: string) => renderUpdatedAtColumn(text),
    },
    {
      title: "Rejected By",
      dataIndex: "updatedBy",
      key: "updatedBy",
      render: (text: string) => renderUpdatedAtColumn(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedBy"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedBy"
            ? "descend"
            : undefined,
    },
    {
      title: "Rejected Reason",
      dataIndex: "rejectReason",
      key: "rejectReason",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "rejectReason"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "rejectReason"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: any) => {
        const name = "approvalManagement";
        return query.get("activeTab") === "pending"
          ? renderActionsColumn(record, undefined, undefined, undefined, undefined, undefined, name)
          : renderActionsColumn(record);
      },
    },
  ];

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";
    if (column.sorter) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (sortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      }
    }
    setOrderByName(column.dataIndex);
    setCurrentPage(0);
    getRecords(order, column.dataIndex, searchString, currentPage);
  };

  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    const unformattedValue = unformatSubModule(value);
    setSearchString(unformattedValue);
    setIsLoading(isLoading);
  }, 1000);

  useEffect(() => {
    getRecords("desc", "createdAt", searchString, currentPage);
  }, [query?.get("activeTab"), actionType]);

  useEffect(() => {
    getRecords(sortOrder, orderByName, searchString, 0);
  }, [searchString]);

  const getColumnsByTab = (activeTab: string) => {
    const filteredColumns = columns.filter((column) => {
      if (typeof column.title === "string") {
        switch (activeTab) {
          case "pending":
            return ["Request Type", "Module", "Sub Module", "Requested At", "Requested By", "Actions"].includes(
              column.title
            );
          case "approved":
            return [
              "Request Type",
              "Module",
              "Sub Module",
              "Approved At",
              "Approved By",
              "Requested By",
              "Requested At",
              "Actions",
            ].includes(column.title);
          case "rejected":
            return [
              "Request Type",
              "Module",
              "Sub Module",
              "Rejected At",
              "Rejected By",
              "Requested By",
              "Requested At",
              "Rejected Reason",
              "Actions",
            ].includes(column.title);
          default:
            return true;
        }
      }
      return false;
    });

    return filteredColumns.map((col: any) => ({
      ...col,
      onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
    }));
  };

  const handleApprove = async () => {
    try {
      const formdata = new FormData();
      formdata.append("approvalId", selectedRowData.approvalId);
      formdata.append("status", "APPROVED");
      put("management/update-status", formdata)
        .then((res: any) => {
          message.success("Approved successfully!");
          getRecords(sortOrder, orderByName, searchString, currentPage);
          setDrawerVisible(false);
        })
        .catch(() => {
          message.error("Failed to approve!");
        });
    } catch (error: any) {
      message.error("Failed to approve!");
    }
  };

  const handleReject = async (reason?: string) => {
    if (!reason) return message.error("Please provide reason for rejection");
    try {
      const formdata = new FormData();
      formdata.append("approvalId", selectedRowData.approvalId);
      formdata.append("status", "REJECTED");
      formdata.append("rejectReason", reason);
      put("management/update-status", formdata)
        .then(() => {
          message.success("Rejected successfully!");
          getRecords(sortOrder, orderByName, searchString, currentPage);
          setDrawerVisible(false);
        })
        .catch(() => {
          message.error("Failed to reject!");
        });
    } catch (error) {
      message.error("Failed to reject!");
    }
  };

  const actions: Action[] = [
    { label: "Approve", onClick: handleApprove, type: "primary" },
    {
      label: "Reject",
      onClick: handleReject,
      type: "ghost",
    },
  ];

  const getTabContent = (tabKey: string) => (
    <>
      <DataTable
        tabOffset={60}
        columns={getColumnsByTab(tabKey)}
        className="table-clickable"
        tableData={data ? data.map((d: any) => ({ ...d })) : []}
        isLoading={isLoading}
        exportFileName="CONTRACT"
        isPagination={true}
        recordsPerPage={10}
        multiSelect={false}
        hideExport={true}
        isServerSearchApi={true}
        searchPlaceholderText={"Search Module / Sub Module"}
        onSearchChange={handleSearchChange}
        onClick={(record) => {
          setSelectedRowData(record);
          setDrawerVisible(true);
        }}
        additionalCTA={
          <ActionDropdown
            value={actionType}
            onChange={setActionType}
          />
        }
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          total={totalRecords}
          onChange={(curPage) => {
            let page = curPage - 1;
            setCurrentPage(page);
            getRecords(sortOrder, orderByName, searchString, page);
          }}
          showSizeChanger={false}
        />
      </div>
    </>
  );

  const items = [
    { label: "Pending", key: "pending", children: getTabContent("pending") },
    { label: "Rejected", key: "rejected", children: getTabContent("rejected") },
    { label: "Approved", key: "approved", children: getTabContent("approved") },
  ];

  const getRecords = (sortOrder: string, orderByParam: string, searchString: any, currentPage?: number) => {
    let activeTab = query?.get("activeTab");
    if (!activeTab) return;
    let currentModule = getCurrentModule();
    setIsLoading(true);
    let url = `management/get-documents?page=${currentPage}&size=${10}&application=${getModulePath(currentModule)}&status=${activeTab}&order=${sortOrder}&orderBy=${orderByParam}&searchText=${searchString}${
      actionType ? `&action=${actionType}` : ""
    }`;
    get(url)
      .then((res: any) => {
        if (res && res.response.data) {
          let envelopes = res.response.data.records;
          setTotalRecords(res.response.data.total);
          setData(envelopes);
        }
        setIsLoading(false);
      })
      .catch(() => {
        setData([]);
        setIsLoading(false);
      });
  };

  return (
    <ContentWrapper title={"Approval Management"}>
      <Tabs
        activeKey={query?.get("activeTab") || ""}
        items={items}
        onChange={(activeKey) => {
          navigate(`${window.location.pathname}?activeTab=${activeKey}`);
          setSearchString("");
          setCurrentPage(0);
          setActionType(undefined); // ✅ reset dropdown filter
        }}
      />
      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={
          drilldownData[selectedRowData?.subModule?.split("_").pop() as keyof typeof drilldownData] ||
          generateFieldsFromPayload(drilldown?.payload)
        }
        actions={query?.get("activeTab") == "pending" ? actions : []}
        data={drilldown?.payload}
        isLoading={drilldownLoading}
        reviewNotes={
          query.get("activeTab") !== "approved" && query.get("activeTab") !== "rejected"
            ? drilldown?.reviewNotes
            : undefined
        }
        copyClipboard={true}
      />
    </ContentWrapper>
  );
};
